import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Copy, CheckCheck, Building2, AlertCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import { api } from '@/lib/api';

interface FormValues {
  name: string;
  slug: string;
  adminEmail: string;
  adminName: string;
}

interface CreateResult {
  workspace: { id: string; slug: string; name: string };
  tempPassword: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

export function WorkspaceDrawer({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [created, setCreated] = useState<CreateResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { name: '', slug: '', adminEmail: '', adminName: '' },
  });

  const nameValue = watch('name');

  useEffect(() => {
    if (!slugTouched) {
      const auto = slugify(nameValue);
      setValue('slug', auto, { shouldValidate: false });
    }
  }, [nameValue, slugTouched, setValue]);

  useEffect(() => {
    if (!open) {
      reset({ name: '', slug: '', adminEmail: '', adminName: '' });
      setCreated(null);
      setCopied(false);
      setSlugTouched(false);
    }
  }, [open, reset]);

  const createMut = useMutation({
    mutationFn: (input: FormValues) => api.post<CreateResult>('/v1/master/workspaces', input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['master', 'workspaces'] });
      setCreated(data);
      toast.success(`Workspace "${data.workspace.name}" criado.`);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar workspace.');
    },
  });

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copiado.');
    } catch {
      toast.error('Falha ao copiar.');
    }
  }

  async function onSubmit(values: FormValues) {
    await createMut.mutateAsync(values);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        {created ? (
          <div className="flex flex-col flex-1 min-h-0">
            <SheetHeader
              eyebrow="Workspace criado"
              title={created.workspace.name}
              description="Guarde a senha temporária agora — não vamos mostrar de novo."
            />
            <SheetBody className="space-y-5">
              <div className="rounded-2 border border-card-stroke bg-card-soft p-4 space-y-3">
                <div>
                  <Eyebrow>Slug</Eyebrow>
                  <p className="font-mono text-sm text-fg mt-0.5">/{created.workspace.slug}</p>
                </div>
                <div>
                  <Eyebrow>ID</Eyebrow>
                  <p className="font-mono text-xs text-fg-muted mt-0.5 break-all">{created.workspace.id}</p>
                </div>
              </div>

              <div className="rounded-3 border border-warn/30 bg-warn-soft/40 p-5">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle className="h-4 w-4 text-warn shrink-0 mt-0.5" strokeWidth={1.8} />
                  <p className="text-sm font-medium text-warn">
                    Senha temporária do admin · só agora
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-[15px] font-semibold text-fg bg-card border border-card-stroke rounded-2 px-3 py-2 select-all">
                    {created.tempPassword}
                  </code>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => copyToClipboard(created.tempPassword)}
                  >
                    {copied ? (
                      <>
                        <CheckCheck className="h-4 w-4" strokeWidth={1.8} />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" strokeWidth={1.8} />
                        Copiar
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-fg-muted mt-3">
                  O admin troca essa senha no primeiro login em <code>/configuracoes</code>.
                </p>
              </div>

              <div className="rounded-2 border border-card-stroke p-4 space-y-2">
                <Eyebrow>Próximos passos</Eyebrow>
                <ul className="text-sm text-fg-muted space-y-1.5">
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint-deep shrink-0" />
                    Compartilhe email + senha temporária com o admin do workspace
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint-deep shrink-0" />
                    Use "Impersonar" pra entrar no workspace e configurar
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-mint-deep shrink-0" />
                    Cadastre entidade, acordo e credenciais do portal
                  </li>
                </ul>
              </div>
            </SheetBody>
            <SheetFooter>
              <Button variant="primary" type="button" onClick={() => onOpenChange(false)}>
                Concluir
              </Button>
            </SheetFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            <SheetHeader
              eyebrow="Novo workspace"
              title="Cadastrar tenant"
              description="Crie um workspace isolado para uma entidade do terceiro setor."
            />
            <SheetBody className="space-y-5">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="grid h-9 w-9 place-items-center rounded-2 bg-mint-soft text-mint-deep">
                    <Building2 className="h-4 w-4" strokeWidth={1.8} />
                  </div>
                  <Pill tone="mint" dot>tenant isolado</Pill>
                </div>
              </div>

              <div>
                <Label htmlFor="name" className="mb-1.5 block">Nome do workspace</Label>
                <Input
                  id="name"
                  placeholder="Ex.: Instituto Esperança"
                  autoComplete="organization"
                  {...register('name', {
                    required: 'Obrigatório',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                    maxLength: { value: 120, message: 'Máximo 120 caracteres' },
                  })}
                />
                {errors.name && <p className="text-[11px] text-warn mt-1">{errors.name.message}</p>}
              </div>

              <div>
                <Label htmlFor="slug" className="mb-1.5 block">Slug (identificador)</Label>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-fg-muted">/</span>
                  <Input
                    id="slug"
                    placeholder="instituto-esperanca"
                    autoComplete="off"
                    {...register('slug', {
                      required: 'Obrigatório',
                      pattern: {
                        value: /^[a-z0-9-]+$/,
                        message: 'Apenas letras minúsculas, números e hífen',
                      },
                      minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                      maxLength: { value: 40, message: 'Máximo 40 caracteres' },
                      onChange: () => setSlugTouched(true),
                    })}
                  />
                </div>
                <p className="text-[11px] text-fg-muted mt-1">
                  Auto-gerado a partir do nome. Pode editar.
                </p>
                {errors.slug && <p className="text-[11px] text-warn mt-1">{errors.slug.message}</p>}
              </div>

              <div className="pt-3 border-t border-card-stroke">
                <Eyebrow>Admin inicial</Eyebrow>
                <p className="text-sm text-fg-muted mt-1 mb-3">
                  Esta pessoa fica como WORKSPACE_ADMIN e poderá convidar outros.
                </p>
              </div>

              <div>
                <Label htmlFor="adminName" className="mb-1.5 block">Nome do admin</Label>
                <Input
                  id="adminName"
                  placeholder="Ex.: Maria Silva"
                  autoComplete="name"
                  {...register('adminName', {
                    required: 'Obrigatório',
                    minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                  })}
                />
                {errors.adminName && (
                  <p className="text-[11px] text-warn mt-1">{errors.adminName.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="adminEmail" className="mb-1.5 block">E-mail do admin</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  placeholder="maria@instituto.org.br"
                  autoComplete="email"
                  {...register('adminEmail', {
                    required: 'Obrigatório',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'E-mail inválido',
                    },
                  })}
                />
                <p className="text-[11px] text-fg-muted mt-1">
                  A senha temporária aparece na próxima tela. Anote!
                </p>
                {errors.adminEmail && (
                  <p className="text-[11px] text-warn mt-1">{errors.adminEmail.message}</p>
                )}
              </div>
            </SheetBody>
            <SheetFooter>
              <SheetClose asChild>
                <Button variant="ghost" type="button">Cancelar</Button>
              </SheetClose>
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                    Criando…
                  </>
                ) : (
                  'Criar workspace'
                )}
              </Button>
            </SheetFooter>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
